import { computed, inject, Injectable, signal } from '@angular/core';
import {
    CheckOutRegisterSessionMovement,
    CheckOutRegisterSessionStatus,
    ICashCount,
    ICashRegisterBalance,
    ICheckOutRegisterSession_FE,
    ICurrentCheckOutRegisterSession,
    Permission
} from '@vierkant-software/types__api';
import { debounceTime } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CheckOutRegisterSessionSyncService } from 'src/app/services/checkOutRegisterSessionSync.service';
import { APIStates, AppService } from './app.service';

const SYNC_RELOAD_DEBOUNCE_MS = 300;
// spreads the reloads of all tills reconnecting at once
const RECONNECT_JITTER_MS = 3000;
const COMMUNICATION_INTERRUPTED = 'COMMUNICATION_INTERRUPTED';

/** the call got no answer: the backend may have booked anyway */
export class UncertainOutcomeError extends Error {
    readonly status = COMMUNICATION_INTERRUPTED;
}

// Timeout or lost connection: the backend may have booked anyway, a repeat could book twice.
export function isUncertainOutcome(error: unknown): boolean {
    if (error === undefined)
        return true;
    const status = (error as { status?: unknown } | null)?.status;
    return status === COMMUNICATION_INTERRUPTED || status === 'IPC:TIMEOUT_RUN_EXCEEDED';
}

export const MOVEMENT_SERIAL_MISMATCH = 'SHOPPINGCART:CHECKOUT_REGISTER_SESSION_MOVEMENT_SERIAL_MISMATCH';

export function isMovementSerialMismatch(error: unknown): boolean {
    return (error as { status?: unknown } | undefined)?.status === MOVEMENT_SERIAL_MISMATCH;
}

const OPEN_MOVEMENT_ERRORS = ['SHOPPINGCART:SESSION_CART_HAS_OPEN_CONTENT', 'SHOPPINGCART:SESSION_CART_HAS_OPEN_MOVEMENT_OF_OTHER_TILL'];
const NO_PENDING_MOVEMENT = 'SHOPPINGCART:CHECKOUT_REGISTER_SESSION_NO_PENDING_MOVEMENT';
const NOT_A_CASH_REGISTER = 'SHOPPINGCART:CHECKOUT_REGISTER_SESSION_NOT_A_CASH_REGISTER';
const SESSION_GONE = ['SHOPPINGCART:CHECKOUT_REGISTER_SESSION_NOT_ACTIVE', 'SHOPPINGCART:CHECKOUT_REGISTER_SESSION_NOT_FOUND'];

export function isNotACashRegister(error: unknown): boolean {
    return (error as { status?: unknown } | undefined)?.status === NOT_A_CASH_REGISTER;
}

/** another till closed the session meanwhile */
export function isSessionGone(error: unknown): boolean {
    return SESSION_GONE.includes((error as { status?: string } | undefined)?.status ?? '');
}

export function isOpenMovementError(error: unknown): boolean {
    return OPEN_MOVEMENT_ERRORS.includes((error as { status?: string } | undefined)?.status ?? '');
}

// Errors the movement dialog shows itself; a toast would be a modal on top of it at the till.
function isHandledByMovementDialog(error: unknown): boolean {
    const status = (error as { status?: unknown } | undefined)?.status;
    return isMovementSerialMismatch(error) || isOpenMovementError(error) || status === NO_PENDING_MOVEMENT;
}

// Minor units, only to show the typed amount while counting; the backend computes the booked amount itself.
export function balanceAmount(balance: ICashRegisterBalance): number {
    const sum = (entries: Record<number, { amount: number }> | undefined, centKeys: boolean): number =>
        Object.entries(entries ?? {}).reduce((total, [key, entry]) => {
            const denomination = centKeys && key.startsWith('0') ? Number(key) : Number(key) * 100;
            return total + denomination * (entry?.amount ?? 0);
        }, 0);
    return sum(balance.coins, true) + sum(balance.bills, false);
}

@Injectable({ providedIn: 'root' })
export class RegisterSessionStateService {
    private app = inject(AppService);

    private readonly current = signal<ICurrentCheckOutRegisterSession | null>(null);
    private readonly failed = signal(false);
    private readonly manager = signal(false);
    private readonly mover = signal(false);
    readonly loadFailed = this.failed.asReadonly();
    /** start, withdrawal and close; the role permission "Kassensitzung" */
    readonly canManage = this.manager.asReadonly();
    /** deposit and Kassensturz, with the till login */
    readonly canMove = this.mover.asReadonly();
    /** a movement needs ShoppingCart_Write, a withdrawal also the session permission */
    readonly canWithdraw = computed(() => this.canMove() && this.canManage());

    readonly loaded = computed(() => this.current() !== null);
    // After a failed reload the last state may be stale, so nothing below shows or decides from it.
    private readonly state = computed(() => this.failed() ? null : this.current());
    readonly session = computed(() => this.state()?.session);
    readonly defaultOpening = computed(() => this.state()?.defaultOpening);
    readonly isOpen = computed(() => this.session()?.status === CheckOutRegisterSessionStatus.open);
    readonly isClosing = computed(() => this.session()?.status === CheckOutRegisterSessionStatus.closing);
    readonly isCashRegister = computed(() => !!this.state()?.isCashRegister);
    // Display only; a dialog takes serial and pending movement from one load, never from here.
    readonly openingPending = computed(() => !!this.state()?.openingPending);
    readonly pendingMovement = computed(() => this.state()?.pendingMovement);
    // A running session must always stay closable, even if cash was removed meanwhile.
    readonly showsSessionUi = computed(() => this.isCashRegister() || !!this.session());
    // Until the state is known the till may offer it; the backend enforces the rule.
    readonly isTabAllowed = computed(() => this.state()?.tabAllowed ?? true);

    private generation = 0;
    private inFlight: Promise<ICurrentCheckOutRegisterSession | undefined> | null = null;
    private running = false;
    private reloadAgain = false;
    private afterAction: Promise<ICurrentCheckOutRegisterSession | undefined> | null = null;

    constructor() {
        if (!environment.isCashRegister)
            return;

        this.app.$APIState.subscribe(state => {
            switch(state.type) {
                case APIStates.loggedIn:
                    this.generation++;
                    this.manager.set(this.app.getPermission(Permission.CheckOutRegisterSession_Write));
                    this.mover.set(this.app.getPermission(Permission.ShoppingCart_Write));
                    void this.reload();
                    break;
                case APIStates.loggedOut:
                    this.generation++;
                    this.current.set(null);
                    this.failed.set(false);
                    this.manager.set(false);
                    this.mover.set(false);
                    break;
            }
        });

        // A resync after a reconnect does not report a session closed in the meantime.
        this.app.$connectionStatus.subscribe(status => {
            if (status !== 'connected' || this.app.state !== APIStates.loggedIn)
                return;
            setTimeout(() => {
                if (this.app.state === APIStates.loggedIn)
                    void this.reload();
            }, Math.random() * RECONNECT_JITTER_MS);
        });

        // The sync carries neither the serial, the open movement nor the allowed payment types.
        inject(CheckOutRegisterSessionSyncService).dataChange$.pipe(debounceTime(SYNC_RELOAD_DEBOUNCE_MS)).subscribe(() => void this.reload());
    }

    /** finishing a pending movement takes the permission of the movement itself */
    canFinish(movement: CheckOutRegisterSessionMovement | undefined): boolean {
        return movement === CheckOutRegisterSessionMovement.withdrawal ? this.canWithdraw() : this.canMove();
    }

    // Until the state is known the configured types apply; the backend enforces the session rule.
    isPaymentTypeAllowed(paymentType: string): boolean {
        const allowed = this.state()?.allowedPaymentTypes_ID ?? this.app.checkoutRegisterConfig?.paymentTypes_ID;
        return allowed?.includes(paymentType) ?? false;
    }

    // Resolves with the state loaded after this call, undefined if that load failed.
    reload(): Promise<ICurrentCheckOutRegisterSession | undefined> {
        if (this.running && this.inFlight !== null) {
            this.reloadAgain = true;
            return this.inFlight;
        }
        this.inFlight = this.runReload();
        return this.inFlight;
    }

    // The load started after the last action; an error path takes its state from it instead of loading again.
    reloadAfterAction(): Promise<ICurrentCheckOutRegisterSession | undefined> {
        return this.afterAction ?? this.reload();
    }

    private async runReload(): Promise<ICurrentCheckOutRegisterSession | undefined> {
        this.running = true;
        let applied: ICurrentCheckOutRegisterSession | undefined;
        try {
            do {
                this.reloadAgain = false;
                applied = undefined;
                if (this.app.state !== APIStates.loggedIn)
                    break;
                const generation = this.generation;
                try {
                    const state = await this.app.api.CheckOutWorker.getCurrentCheckOutRegisterSession();
                    if (generation !== this.generation)
                        continue;
                    this.current.set(state);
                    this.failed.set(false);
                    applied = state;
                } catch(e) {
                    console.warn('[RegisterSessionStateService] Failed to load the session:', e);
                    if (generation === this.generation)
                        this.failed.set(true);
                }
            } while (this.reloadAgain);
        } finally {
            this.running = false;
        }
        return applied;
    }

    async open(balance: ICashRegisterBalance): Promise<ICheckOutRegisterSession_FE> {
        try {
            return await this.run(() => this.app.api.CheckOutWorker.openCheckOutRegisterSession(balance));
        } catch(e) {
            // the payment buttons read the till config; it may have lost cash meanwhile
            if (isNotACashRegister(e)) {
                await this.app.reloadCheckoutRegisterConfig()
                    .catch((err: unknown) => console.warn('[RegisterSessionStateService] Failed to reload the till config:', err));
            }
            throw e;
        }
    }

    /** a repeat with the same `requestKey` books at most once */
    async deposit(balance: ICashRegisterBalance, movementSerial: number, requestKey: string): Promise<ICheckOutRegisterSession_FE> {
        return await this.run(() => this.app.api.CheckOutWorker.addCheckOutRegisterSessionMovement(
            CheckOutRegisterSessionMovement.deposit, balance, movementSerial, requestKey), true);
    }

    /** a repeat with the same `requestKey` books at most once */
    async withdraw(balance: ICashRegisterBalance, movementSerial: number, requestKey: string): Promise<ICheckOutRegisterSession_FE> {
        return await this.run(() => this.app.api.CheckOutWorker.addCheckOutRegisterSessionMovement(
            CheckOutRegisterSessionMovement.withdrawal, balance, movementSerial, requestKey), true);
    }

    async finishPendingMovement(movementSerial: number): Promise<ICheckOutRegisterSession_FE> {
        return await this.run(() => this.app.api.CheckOutWorker.finishCheckOutRegisterSessionMovement(movementSerial), true);
    }

    async count(balance: ICashRegisterBalance): Promise<ICashCount> {
        return await this.run(() => this.app.api.CheckOutWorker.countCheckOutRegisterSession(balance));
    }

    /** `recount` replaces the count of a close stuck in `closing` */
    async close(balance: ICashRegisterBalance, recount = false): Promise<ICheckOutRegisterSession_FE> {
        return await this.run(() => this.app.api.CheckOutWorker.closeCheckOutRegisterSession(balance, recount));
    }

    private async run<T>(action: () => Promise<T>, movementDialog = false): Promise<T> {
        try {
            const result = await action();
            // The RPC client resolves a timed out call with undefined instead of rejecting it.
            if (result === undefined)
                throw new UncertainOutcomeError('no answer from the backend');
            return result;
        } catch(e) {
            if (!isUncertainOutcome(e) && !(movementDialog && isHandledByMovementDialog(e)))
                e.displayErrorToast?.();
            throw e;
        } finally {
            this.afterAction = this.reload();
        }
    }
}
